import { formatOrderDate } from './orderDate'

import type { OrderItem } from '../components/details/types'
import type { LabelFunction } from '../i18n'

/**
 * Resolves the subtitle displayed in the order header.
 *
 * 1. trackingInfo.deliveredDate present       → "Delivered on {most recent deliveredDate}"
 * 2. promisedDeliveryDate present (any item)  → "Delivery by {latest promisedDeliveryDate}"
 * 3. Fallback                                 → "Ordered on {orderDate}"
 *
 * `orderDate` arrives already formatted by the caller, so only the tracking
 * dates are formatted here — hence the `locale`.
 */
export function resolveOrderSubtitle(
  items: OrderItem[],
  orderDate: string,
  label: LabelFunction,
  locale: string,
): string {
  const allTracking = items.flatMap((item) => item.trackingInfo ?? [])
  const deliveredDates = allTracking
    .map((t) => t.deliveredDate)
    .filter((d): d is string => typeof d === 'string' && d.length > 0)

  if (deliveredDates.length > 0) {
    const latest = deliveredDates.reduce((a, b) =>
      new Date(b) > new Date(a) ? b : a,
    )
    return label('orderList.subtitle.deliveredOn', {
      date: formatOrderDate(latest, locale),
    })
  }

  const promisedDates = items
    .map((item) => item.promisedDeliveryDate)
    .filter((d): d is string => typeof d === 'string' && d.length > 0)

  if (promisedDates.length > 0) {
    const latest = promisedDates.reduce((a, b) =>
      new Date(b) > new Date(a) ? b : a,
    )
    return label('orderList.subtitle.deliveryBy', {
      date: formatOrderDate(latest, locale),
    })
  }

  return label('orderList.subtitle.orderedOn', { date: orderDate })
}
